import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Chip, Stack, Paper, Grid, Tooltip, Alert, CircularProgress, useTheme, useMediaQuery, Tabs, Tab, BottomNavigation, BottomNavigationAction } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PublicIcon from '@mui/icons-material/Public';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import useModels from '../hooks/useModels';
import NewModelDialog from '../components/common/NewModelDialog';
import EditModelDialog from '../components/common/EditModelDialog';
import ViewModelDialog from '../components/common/ViewModelDialog';
import modelsService from '../services/modelsService';
import { isUserAdmin, getUserProfile } from '../firebase/services/userService';
import UniversalCard from '../components/common/UniversalCard';

const ModelsListPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addDeleteNotification, addActionErrorNotification } = useNotifications();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // État local pour le filtre, par défaut sur "all"
  const [filter, setFilter] = useState('all');
  const [showNewModelDialog, setShowNewModelDialog] = useState(false);
  const [showEditModelDialog, setShowEditModelDialog] = useState(false);
  const [showViewModelDialog, setShowViewModelDialog] = useState(false);
  const [editingModelId, setEditingModelId] = useState(null);
  const [viewingModelId, setViewingModelId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  
  const { models, loading, error } = useModels(filter, currentUser?.uid);

  // Gérer le premier chargement vs changements de filtre
  useEffect(() => {
    if (!loading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [loading, isInitialLoad]);

  // Charger les profils des utilisateurs pour afficher leurs noms
  useEffect(() => {
    const loadUserProfiles = async () => {
      if (!models || models.length === 0) return;

      const userIds = [...new Set(models.map(model => model.createdBy))];
      const profilesPromises = userIds.map(async (userId) => {
        if (userId === currentUser?.uid || userId === 'Admin') return null;
        
        try {
          const profile = await getUserProfile(userId);
          return { userId, profile };
        } catch (error) {

          return null;
        }
      });

      const profilesResults = await Promise.all(profilesPromises);
      const newProfiles = {};
      
      profilesResults.forEach(result => {
        if (result && result.profile) {
          newProfiles[result.userId] = result.profile;
        }
      });

      if (Object.keys(newProfiles).length > 0) {
        setUserProfiles(prev => ({ ...prev, ...newProfiles }));
      }
    };

    loadUserProfiles();
  }, [models, currentUser?.uid]);

  // Vérifier si l'utilisateur est admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        setIsCheckingAdmin(false);
        return;
      }

      try {
        const adminStatus = await isUserAdmin(currentUser.uid);
        setIsAdmin(adminStatus);
      } catch (error) {

        setIsAdmin(false);
      } finally {
        setIsCheckingAdmin(false);
      }
    };

    checkAdminStatus();
  }, [currentUser]);

  const handleFilterChange = (newFilter) => {
    // Empêcher la désélection complète - toujours avoir un filtre actif
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ce modèle ?')) {
      try {
        // Récupérer le nom du modèle avant suppression
        const model = models.find(m => m.id === id);
        const modelName = model?.title || 'Modèle inconnu';
        
        await modelsService.deleteModel(id);
        await addDeleteNotification(modelName, 'Modèle');
      } catch (error) {

        await addActionErrorNotification('la suppression du modèle', error.message || 'Erreur inconnue');
      }
    }
  };

  const handleEdit = (modelId) => {
    setEditingModelId(modelId);
    setShowEditModelDialog(true);
  };

  const handleView = (modelId) => {
    setViewingModelId(modelId);
    setShowViewModelDialog(true);
  };

  const canEditOrDelete = (model) => {
    // Les admins peuvent modifier/supprimer tous les modèles
    if (isAdmin) return true;
    // Les utilisateurs normaux ne peuvent modifier que leurs propres modèles
    return filter === 'mine' || model.createdBy === currentUser?.uid;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    try {
      // Gestion des timestamps Firebase
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  const handleNewModelSuccess = (modelId) => {
    // Si on est dans "Tous les modèles", basculer vers "Mes modèles" pour voir le nouveau modèle
    if (filter === 'all') {
      setFilter('mine');
    }
  };

  const handleEditSuccess = (modelId) => {
    // Le hook useModels se chargera de la mise à jour en temps réel
  };

  const getCreatorDisplayName = (model) => {
    if (model.createdBy === currentUser?.uid) {
      return 'Vous';
    } else if (model.createdBy === 'Admin') {
      return 'Admin';
    } else {
      // Utiliser le profil chargé pour afficher le nom complet
      const userProfile = userProfiles[model.createdBy];
      if (userProfile) {
        // Priorité au displayName, puis firstName + lastName, puis email, puis ID
        if (userProfile.displayName) {
          return userProfile.displayName;
        } else if (userProfile.firstName && userProfile.lastName) {
          return `${userProfile.firstName} ${userProfile.lastName}`;
        } else if (userProfile.email) {
          return userProfile.email;
        }
      }
      // Fallback : afficher seulement les premiers caractères de l'ID utilisateur
      return model.createdBy.length > 12 ? `${model.createdBy.substring(0, 12)}...` : model.createdBy;
    }
  };

  if ((loading && isInitialLoad) || isCheckingAdmin) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: 2, pb: isXs ? 4 : 4 }}>
        <Grid container spacing={2}>
          {[...Array(6)].map((_, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <UniversalCard 
                type="model"
                isLoading={true}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', py: 4 }}>
        <Alert severity="error">
          Erreur lors du chargement des modèles : {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4, px: 2, pb: isXs ? 10 : 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        mb: 4,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 3, sm: 4 }
      }}>
        <Box sx={{ flex: 1, mr: { xs: 0, sm: 4 } }}>
          {/* Description élégante de la page */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #fffbf0 0%, #fff3e0 100%)',
            borderRadius: 3,
            p: 3,
            border: '1px solid #fff3e0',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
              borderRadius: '50%',
              opacity: 0.1,
              transform: 'translate(20px, -20px)'
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile && !infoExpanded ? 0 : 1.5, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography 
                    variant="h5" 
                    fontWeight={700}
                    sx={{ 
                      color: '#2c3e50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}
                  >
                    🤖 Modèles
                  </Typography>
                  {isAdmin && (
                    <Tooltip title="Vous êtes administrateur - vous pouvez modifier tous les modèles">
                      <AdminPanelSettingsIcon color="primary" data-testid="admin-icon" />
                    </Tooltip>
                  )}
                </Box>
                {isMobile && (
                  <IconButton 
                    size="small" 
                    onClick={() => setInfoExpanded(!infoExpanded)}
                    sx={{ 
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                      transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {(!isMobile || infoExpanded) && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#546e7a',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    maxWidth: '600px'
                  }}
                >
                  Créez des <strong>modèles de réunion réutilisables</strong> incluant les <strong>participants récurrents</strong> et les <strong>instructions personnalisées</strong> pour l'IA. Simplifiez la création de vos réunions en pré-configurant les éléments qui reviennent souvent : équipes, formats de résumé et contextes spécifiques.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        
        {/* Bouton Nouveau modèle pour desktop */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowNewModelDialog(true)}
          sx={{
            display: { xs: 'none', sm: 'flex' }, // Cacher sur mobile
            borderRadius: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9rem',
            background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)',
            boxShadow: `0 6px 20px rgba(63, 81, 181, 0.25), 
                       0 3px 10px rgba(63, 81, 181, 0.15),
                       inset 0 1px 0 rgba(255,255,255,0.2)`,
            border: '1px solid rgba(255,255,255,0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              transition: 'left 0.6s',
            },
            '&:hover': {
              background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 50%, #5C6BC0 100%)',
              transform: 'translateY(-2px) scale(1.01)',
              boxShadow: `0 8px 25px rgba(63, 81, 181, 0.35), 
                         0 4px 15px rgba(63, 81, 181, 0.25),
                         inset 0 1px 0 rgba(255,255,255,0.3)`,
              '&::before': {
                left: '100%'
              },
              '& .MuiSvgIcon-root': {
                transform: 'rotate(180deg) scale(1.05)'
              }
            },
            '& .MuiSvgIcon-root': {
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              fontSize: '1.1rem'
            },
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          Nouveau modèle
        </Button>
      </Box>

      {/* Onglets pour desktop */}
      {!isXs && (
        <Paper elevation={0} sx={{ 
          mb: 3, 
          borderRadius: 2, 
          border: '1px solid', 
          borderColor: 'divider',
          overflow: 'hidden'
        }}>
          <Tabs 
            value={filter} 
            onChange={(e, newValue) => handleFilterChange(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                minHeight: 56,
                py: 2
              }
            }}
          >
            <Tab 
              icon={<PublicIcon />} 
              iconPosition="start"
              label="Tous les modèles" 
              value="all"
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={<PersonIcon />} 
              iconPosition="start"
              label="Mes modèles" 
              value="mine"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Paper>
      )}
      
      {models.length === 0 ? (
        <Paper elevation={0} sx={{ my: 8, textAlign: 'center', py: 6, px: 3, borderRadius: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {filter === 'mine' ? 'Aucun modèle créé' : 'Aucun modèle disponible'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {filter === 'mine' 
              ? 'Créez votre premier modèle pour simplifier la création de vos réunions récurrentes' 
              : 'Aucun modèle n\'a encore été partagé par la communauté'
            }
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {models.map((model) => (
            <Grid item xs={12} sm={6} md={4} key={model.id}>
              <UniversalCard 
                type="model"
                item={model}
                actions={[
                  {
                    type: 'view',
                    icon: <VisibilityIcon fontSize="small" />,
                    tooltip: 'Consulter le modèle',
                    color: 'info'
                  },
                  ...(canEditOrDelete(model) ? [
                    {
                      type: 'edit',
                      icon: <EditIcon fontSize="small" />,
                      tooltip: 'Modifier',
                      color: 'primary'
                    },
                    {
                      type: 'delete',
                      icon: <DeleteIcon fontSize="small" />,
                      tooltip: 'Supprimer',
                      color: 'error'
                    }
                  ] : [])
                ]}
                onAction={(action, item) => {
                  switch (action) {
                    case 'view':
                      handleView(item.id);
                      break;
                    case 'edit':
                      handleEdit(item.id);
                      break;
                    case 'delete':
                      handleDelete(item.id);
                      break;
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog de création de nouveau modèle */}
      <NewModelDialog
        open={showNewModelDialog}
        onClose={() => setShowNewModelDialog(false)}
        onSuccess={handleNewModelSuccess}
      />

      {/* Dialog d'édition de modèle */}
      <EditModelDialog
        open={showEditModelDialog}
        onClose={() => {
          setShowEditModelDialog(false);
          setEditingModelId(null);
        }}
        onSuccess={handleEditSuccess}
        modelId={editingModelId}
        isAdmin={isAdmin}
      />

      {/* Dialog de visualisation de modèle */}
      <ViewModelDialog
        open={showViewModelDialog}
        onClose={() => {
          setShowViewModelDialog(false);
          setViewingModelId(null);
        }}
        modelId={viewingModelId}
      />

      {/* Bottom Navigation pour mobile */}
      {isXs && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            borderTop: '1px solid',
            borderColor: 'divider'
          }} 
          elevation={8}
        >
          <BottomNavigation
            value={filter}
            onChange={(e, newValue) => handleFilterChange(newValue)}
            sx={{
              '& .MuiBottomNavigationAction-root': {
                fontSize: '0.7rem',
                minWidth: 'auto',
                paddingTop: '6px'
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem'
                }
              }
            }}
          >
            <BottomNavigationAction
              label="Tous"
              value="all"
              icon={<PublicIcon />}
            />
            <BottomNavigationAction
              label="Mes modèles"
              value="mine"
              icon={<PersonIcon />}
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default ModelsListPage; 